import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { wishlistService, Wishlist, WishlistItem } from '@/services/wishlistService';

/**
 * Wishlist State Interface
 */
interface WishlistState {
  wishlist: Wishlist | null;
  isLoading: boolean;
  error: string | null;
  itemCount: number;
}

/**
 * Initial state
 */
const initialState: WishlistState = {
  wishlist: null,
  isLoading: false,
  error: null,
  itemCount: 0,
};

/**
 * Fetch user's wishlist
 */
export const fetchWishlist = createAsyncThunk<Wishlist, void, { rejectValue: string }>(
  'wishlist/fetchWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await wishlistService.getWishlist();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch wishlist'
      );
    }
  }
);

/**
 * Add product to wishlist
 */
export const addProductToWishlist = createAsyncThunk<
  Wishlist,
  string,
  { rejectValue: string }
>('wishlist/addProduct', async (productId, { rejectWithValue }) => {
  try {
    const response = await wishlistService.addProduct(productId);
    return response.data;
  } catch (error: any) {
    // Extract error message from API response
    let errorMessage = 'Failed to add product to wishlist';
    
    if (error?.response?.data) {
      // Check for validation errors array
      if (Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0]?.msg || error.response.data.errors[0] || errorMessage;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    return rejectWithValue(errorMessage);
  }
});

/**
 * Add service to wishlist
 */
export const addServiceToWishlist = createAsyncThunk<
  Wishlist,
  string,
  { rejectValue: string }
>('wishlist/addService', async (serviceId, { rejectWithValue }) => {
  try {
    const response = await wishlistService.addService(serviceId);
    return response.data;
  } catch (error: any) {
    // Extract error message from API response
    let errorMessage = 'Failed to add service to wishlist';
    
    if (error?.response?.data) {
      // Check for validation errors array
      if (Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors[0]?.msg || error.response.data.errors[0] || errorMessage;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    return rejectWithValue(errorMessage);
  }
});

/**
 * Remove item from wishlist
 */
export const removeFromWishlist = createAsyncThunk<
  Wishlist,
  string,
  { rejectValue: string }
>('wishlist/removeItem', async (itemId, { rejectWithValue }) => {
  try {
    const response = await wishlistService.removeItem(itemId);
    return response.data;
  } catch (error: any) {
    // Extract error message from API response
    const errorMessage = 
      error?.response?.data?.message || 
      error?.response?.data?.error ||
      error?.message || 
      'Failed to remove item from wishlist';
    return rejectWithValue(errorMessage);
  }
});

/**
 * Move item to cart
 */
export const moveToCart = createAsyncThunk<
  { wishlist: Wishlist; cart: any },
  string,
  { rejectValue: string }
>('wishlist/moveToCart', async (itemId, { rejectWithValue }) => {
  try {
    const response = await wishlistService.moveToCart(itemId);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to move item to cart'
    );
  }
});

/**
 * Check if item is in wishlist
 */
export const checkWishlistStatus = createAsyncThunk<
  { productId?: string; serviceId?: string; isInWishlist: boolean },
  { productId?: string; serviceId?: string },
  { rejectValue: string }
>('wishlist/checkStatus', async ({ productId, serviceId }, { rejectWithValue }) => {
  try {
    const response = await wishlistService.getWishlist();
    const wishlist = response.data;
    
    const isInWishlist = wishlist.items.some((item) => {
      if (productId) {
        return item.itemType === 'product' && item.itemId.toString() === productId;
      }
      if (serviceId) {
        return item.itemType === 'service' && item.itemId.toString() === serviceId;
      }
      return false;
    });

    return { productId, serviceId, isInWishlist };
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to check wishlist status'
    );
  }
});

/**
 * Wishlist slice
 */
const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    setWishlist: (state, action: PayloadAction<Wishlist | null>) => {
      state.wishlist = action.payload;
      state.itemCount = action.payload?.items?.length || 0;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetWishlist: (state) => {
      state.wishlist = null;
      state.itemCount = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch wishlist
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wishlist = action.payload;
        state.itemCount = action.payload.items?.length || 0;
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch wishlist';
      });

    // Add product
    builder
      .addCase(addProductToWishlist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addProductToWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wishlist = action.payload;
        state.itemCount = action.payload.items?.length || 0;
      })
      .addCase(addProductToWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to add product to wishlist';
      });

    // Add service
    builder
      .addCase(addServiceToWishlist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addServiceToWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wishlist = action.payload;
        state.itemCount = action.payload.items?.length || 0;
      })
      .addCase(addServiceToWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to add service to wishlist';
      });

    // Remove item
    builder
      .addCase(removeFromWishlist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wishlist = action.payload;
        state.itemCount = action.payload.items?.length || 0;
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to remove item from wishlist';
      });

    // Move to cart
    builder
      .addCase(moveToCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(moveToCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wishlist = action.payload.wishlist;
        state.itemCount = action.payload.wishlist.items?.length || 0;
      })
      .addCase(moveToCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to move item to cart';
      });
  },
});

// Export actions
export const { setWishlist, clearError, resetWishlist } = wishlistSlice.actions;

// Export selectors
export const selectWishlist = (state: { wishlist: WishlistState }) => state.wishlist.wishlist;
export const selectWishlistLoading = (state: { wishlist: WishlistState }) => state.wishlist.isLoading;
export const selectWishlistError = (state: { wishlist: WishlistState }) => state.wishlist.error;
export const selectWishlistItemCount = (state: { wishlist: WishlistState }) => state.wishlist.itemCount;
export const selectIsInWishlist = (itemId: string, itemType: 'product' | 'service') => {
  return (state: { wishlist: WishlistState }) => {
    if (!state.wishlist.wishlist || !itemId) return false;
    
    // Normalize the itemId to string for comparison
    const normalizedItemId = itemId.toString();
    
    return state.wishlist.wishlist.items.some((item) => {
      // Check item type first
      if (item.itemType !== itemType) return false;
      
      // Handle different itemId formats
      let itemIdStr: string;
      if (typeof item.itemId === 'string') {
        itemIdStr = item.itemId;
      } else if (item.itemId && typeof item.itemId === 'object') {
        // Populated object - check _id or id field
        itemIdStr = (item.itemId as any)?._id?.toString() || (item.itemId as any)?.id?.toString() || '';
      } else {
        itemIdStr = item.itemId?.toString() || '';
      }
      
      // Compare normalized IDs
      return itemIdStr === normalizedItemId;
    });
  };
};

export default wishlistSlice.reducer;
